import React, { Suspense, type ErrorInfo } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import * as serviceWorker from "./serviceWorker";
import "./locales";
import { SafetyBadgePublicPage } from "./safety/public/SafetyBadgePublicPage";
import { ChunkErrorBoundary } from "./ui/ChunkErrorBoundary";
import {
  isChunkReloadPending,
  registerChunkPreloadErrorReload,
} from "./utils/chunk-reload";
import {
  VITE_REACT_APP_SENTRY_DSN,
  VITE_REACT_APP_MELA_ENV,
} from "./constants/variables";

// The full authenticated app (with Apollo) is loaded lazily so the public
// worker-badge route below never pulls it — or the Apollo client — into its
// bundle.
const AuthenticatedApp = React.lazy(() => import("./AuthenticatedApp"));

registerChunkPreloadErrorReload();

// The public worker badge (`/badge/<token>`, opened by scanning a worker's QR)
// is a login-free page for external third parties. Keep it fully isolated from
// the authenticated app: no Apollo, no analytics, and no Sentry PII/IP capture
// of anonymous scanners.
const isPublicBadgeRoute = window.location.pathname.startsWith("/badge/");

const sentryModule = isPublicBadgeRoute ? null : import("@sentry/react");

if (!isPublicBadgeRoute) {
  // Loaded dynamically so an anonymous badge scanner never even imports the
  // analytics/logger chain — which stamps a tracking deviceId into
  // localStorage at import time — nor Sentry's PII/IP capture.
  void import("@/analytics").then((m) => m.default.init());
  void sentryModule?.then((Sentry) =>
    Sentry.init({
      dsn: VITE_REACT_APP_SENTRY_DSN,
      // Setting this option to true will send default PII data to Sentry.
      // For example, automatic IP address collection on events
      sendDefaultPii: true,
      // Adjust Sentry options based on environment
      environment: VITE_REACT_APP_MELA_ENV,
      tracesSampleRate: VITE_REACT_APP_MELA_ENV === "prod" ? 0.2 : 1.0, // Adjust sampling rate based on environment
      // A chunk that failed to load is recovered by a reload; errors raised
      // while that reload is under way are expected and not actionable.
      beforeSend: (event) => (isChunkReloadPending() ? null : event),
    }),
  );
}

const container = document.getElementById("root");
// Errors reaching the root otherwise land in Sentry without a component stack
// (e.g. "Maximum update depth exceeded" with only React and graphql-ws frames).
// Replacing React's default reporting keeps a single report per error.
const reportRootError =
  (handled: boolean) => (error: unknown, errorInfo: ErrorInfo) => {
    console.error(error);
    void sentryModule?.then((Sentry) =>
      Sentry.captureReactException(error, errorInfo, {
        mechanism: {
          type: handled ? "react.caught" : "react.uncaught",
          handled,
        },
      }),
    );
  };

const root = createRoot(container, {
  onUncaughtError: reportRootError(false),
  onCaughtError: reportRootError(true),
});
root.render(
  <ChunkErrorBoundary>
    <Suspense fallback="">
      {isPublicBadgeRoute ? <SafetyBadgePublicPage /> : <AuthenticatedApp />}
    </Suspense>
  </ChunkErrorBoundary>,
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
